import { AttributeKeyValue } from "components/FilteredDropdown";
import { AttributeOptionDetails, Configurator } from "interfaces/products";
import { ParsedUrlQueryInput } from "querystring";

/**
 * A user has just changed a value in an attribute option dropdown.
 * What should we do with this value?
 *
 * 1. Add a new attribute to the query
 * 2. Clear an existing attribute from the query
 * 3. Replace an existing attribute in the query
 *
 * Complications:
 * For 90% of cases, the product attribute ID would be enough to handle this. But some dropdowns
 * have two product attribute IDs, and some products have different dropdowns with the same
 * product attribute ID.
 *
 * Strategy:
 * Ignore product attribute IDs and focus on the attribute option IDs themselves. It is possible
 * for an attribute option to be in two dropdowns, but that would almost certainly be a mistake.
 */
export const buildNewQuery = (
    query: URLSearchParams,
    configurators: Configurator[],
    attributeKeyValue: AttributeKeyValue,
) => {
    let newQuery = [...query.entries()];
    /**
     * Given `443=371339`, this is the `443` part.
     * Using this to build the param that the backend needs, and also for its potential
     * null value, which is a bit hacky but signals that the attribute option ID needs
     * removed from the query.
     */
    const productAttributeId = attributeKeyValue[0];
    /**
     * Given `443=371339`, this is the `371339` part
     */
    const attributeOptionId = attributeKeyValue[1];

    /** Figure out which dropdown the option came from */
    const configuratorMatch = configurators.find((configurator) =>
        configurator.options.find((option) => option.id === attributeOptionId),
    );

    if (productAttributeId === null) {
        /** Pull this attributeOptionId from the query */
        newQuery = newQuery.filter(
            (entry) => entry[1] !== `${attributeOptionId}`,
        );
    } else {
        /** This should never be false unless someone is screwing with the URL manually */
        if (configuratorMatch) {
            const existingSelectionFromDropdownInQuery = newQuery.find(
                (attribute) =>
                    configuratorMatch.options.find(
                        (option) => `${option.id}` === attribute[1],
                    ),
            );
            if (existingSelectionFromDropdownInQuery) {
                /** Then pull it from the query */
                newQuery = newQuery.filter(
                    (entry) =>
                        entry[1] !==
                        `${existingSelectionFromDropdownInQuery[1]}`,
                );
            }
            /** Add the new key/value pair to the query */
            newQuery = newQuery.concat([
                [`${productAttributeId}`, `${attributeOptionId}`],
            ]);
        }
    }

    return new URLSearchParams(newQuery);
};

/**
 * URLSearchParams allows for duplicate keys; JS objects do not. NextJS's router uses
 * ParsedUrlQueryInput, and solves this problem by grouping values with the same key into
 * arrays. So we need to prep it for that.
 */
export const urlSearchParamsToQueryStringInput: (
    query: URLSearchParams,
) => ParsedUrlQueryInput = (query) => {
    /**
     * If the current key/value pair isn't in the new query, add it.
     * If it is and the key's value is a string, make the value an array and add to
     * If it is and the key's value is an array, add to that array
     */
    const modifiedQuery = [...query.entries()].reduce(
        (accumulated, current) => {
            const match = accumulated.findIndex(
                (query) => query[0] === current[0],
            );
            if (match === -1) {
                accumulated.push(current);
            } else {
                const matchValue = accumulated[match][1];
                if (typeof matchValue === "string") {
                    accumulated[match][1] = [matchValue, current[1]];
                } else {
                    (accumulated[match][1] as string[]).push(current[1]);
                }
            }
            return accumulated;
        },
        [] as [string, string | string[]][],
    );

    return Object.fromEntries(modifiedQuery);
};

/**
 * Param order matters for building config_string in backend. Use order of configurators to
 * order the attributes correctly.
 */
export const buildOrderedUrlParamMap = (
    state: URLSearchParams,
    details: AttributeOptionDetails,
) => {
    /** This format allows us to work with duplicate keys */
    const orderedParams: [string, string][] = [];
    /** Product Family always goes first */
    orderedParams.push([`${details.product_attribute_id}`, `${details.id}`]);
    /** Look at configurator ordering to determine how to order the URLSearchParams */
    details.configurator
        /** Simplify. Returns arrays of option IDs as strings  */
        .map((configurator) =>
            configurator.options.map((option) => `${option.id}`),
        )
        .forEach((configurator) => {
            /** Match state to the option IDs pulled from configurator */
            const match = [...state.entries()].find((item) =>
                configurator.find((id) => item[1] === id),
            );
            /** Add to orderedParams! */
            !!match && orderedParams.push([match[0], match[1]]);
        });

    return new URLSearchParams([...orderedParams]);
};
